import type { FeedItem } from '../model/feed.ts';
import type { VideoSummary } from '../model/video.ts';
import { err, ok, parseError, type ParsedList, type ParseError, type Result } from '../result.ts';
import { parseChannelSummary } from './channel.ts';
import type { ParseContext } from './context.ts';
import { parsePlaylistSummary } from './playlist.ts';
import { asArray, asObject, asString, field } from './raw.ts';
import { parseVideoSummary, parseVideoSummaryList } from './video.ts';

/**
 * The API `type` values this build recognises, grouped by the `kind` **we** own (R3).
 *
 * `shortVideo` sits with `video` on purpose: §2 trap 6 — it is *not* a YouTube Short but
 * Invidious's abbreviated `ChannelVideo` serialization, and `/api/v1/popular` returns half-hour
 * videos under it. There is no Shorts discriminator in this API at all.
 */
const VIDEO_TYPES: readonly string[] = ['video', 'shortVideo'];
const CHANNEL_TYPES: readonly string[] = ['channel'];
const PLAYLIST_TYPES: readonly string[] = ['playlist', 'invidiousPlaylist'];

/**
 * R3/R4 — one heterogeneous listing row.
 *
 * Two failure modes that must stay distinguishable, and this is the whole reason the `unknown` arm
 * exists separately from `ParsedList.dropped`:
 *
 * - **A type we don't know** → `ok({kind:'unknown', rawType})`. The API grew something; the row is
 *   skippable but the list keeps its shape and the new type is reportable rather than invisible.
 * - **A type we know, in a record we cannot use** → `err(...)`. That is a broken record, and R4
 *   says it drops itself *and gets counted*.
 *
 * Matching is on `type` alone — no structural guessing. A future `type` that happens to carry a
 * `videoId` could plausibly be coerced into `kind:'video'`, but doing so would silently consume the
 * one signal that says the API changed, which is exactly what this arm is for.
 */
export function parseFeedItem(raw: unknown, ctx: ParseContext): Result<FeedItem, ParseError> {
  const source = asObject(raw);
  if (!source) return err(parseError('FeedItem', null, 'wrong-type'));

  const rawType = asString(field(source, 'type')) ?? '';

  if (VIDEO_TYPES.includes(rawType)) {
    const parsed = parseVideoSummary(raw, ctx);
    return parsed.ok ? ok({ kind: 'video', video: parsed.value }) : err(parsed.error);
  }
  if (CHANNEL_TYPES.includes(rawType)) {
    const parsed = parseChannelSummary(raw, ctx);
    return parsed.ok ? ok({ kind: 'channel', channel: parsed.value }) : err(parsed.error);
  }
  if (PLAYLIST_TYPES.includes(rawType)) {
    const parsed = parsePlaylistSummary(raw, ctx);
    return parsed.ok ? ok({ kind: 'playlist', playlist: parsed.value }) : err(parsed.error);
  }

  return ok({ kind: 'unknown', rawType });
}

export function parseFeedItems(raw: unknown, ctx: ParseContext): ParsedList<FeedItem> {
  const array = asArray(raw);
  if (!array) return { items: [], dropped: [parseError('FeedItemList', null, 'wrong-type')] };
  const items: FeedItem[] = [];
  const dropped: ParseError[] = [];
  for (const entry of array) {
    const parsed = parseFeedItem(entry, ctx);
    if (parsed.ok) items.push(parsed.value);
    else dropped.push(parsed.error);
  }
  return { items, dropped };
}

/**
 * `GET /api/v1/search` — a bare JSON array, not an envelope. Heterogeneous even when `type=video`
 * was requested, because a `200` never proves a parameter was honoured (§2).
 *
 * The validated filter set is handled elsewhere, but for the record: the sort parameter is `sort`
 * with values `relevance|views` only, and `sort_by` is *silently ignored* here while being the
 * correct spelling for comments.
 */
export function parseSearchResults(raw: unknown, ctx: ParseContext): ParsedList<FeedItem> {
  return parseFeedItems(raw, ctx);
}

/**
 * `GET /api/v1/popular` — a bare array, homogeneously `type: "shortVideo"`, and every entry is a
 * plain video. It gets a `VideoSummary` list rather than a `FeedItem` list because the endpoint
 * cannot return anything else, and a caller forced to switch on `kind` for a single-kind feed would
 * be writing dead branches.
 *
 * The records are thinner than search's: no `authorVerified`, no `liveNow`, no `isUpcoming`. Those
 * default to `false` — which is correct for `/popular` (it never lists a live or upcoming video),
 * and is why the defaults live in `parseVideoSummary` rather than at each call site.
 */
export function parsePopularFeed(raw: unknown, ctx: ParseContext): ParsedList<VideoSummary> {
  return parseVideoFeed(raw, ctx, 'PopularFeed');
}

/**
 * `GET /api/v1/trending?region=XX` — also a bare array, `type: "video"`, and structurally the
 * *search* record rather than `/popular`'s thin one: 26 keys including `authorVerified`,
 * `authorThumbnails`, `description` and the quality flags.
 *
 * Structurally, and only structurally. What the rows *contain* is documented separately, where
 * the app acts on it (`feed/listing.ts`): on 2026-07-29 every row of the default
 * tab carried `lengthSeconds: 0`, `viewCount: 0` and a `published` equal to the request's own
 * timestamp, while `?type=gaming` carried 50 complete rows from the same endpoint. Both fixtures are
 * committed, and neither is malformed — a row with no duration is a row this parser must return
 * faithfully rather than drop, because R4's `dropped` count means "we could not use this record" and
 * a thin trending row is perfectly usable as a title, an author and a thumbnail.
 *
 * `region` is validated **before** the call (`feed/region.ts`): an unsupported one answers 500.
 */
export function parseTrendingFeed(raw: unknown, ctx: ParseContext): ParsedList<VideoSummary> {
  return parseVideoFeed(raw, ctx, 'TrendingFeed');
}

/**
 * The two single-kind feeds share everything but the entity name in their error, and the name is
 * worth keeping distinct: "PopularFeed is wrong-type" and "TrendingFeed is wrong-type" land in the
 * same UI, and only one of them tells you which request to go and look at.
 */
function parseVideoFeed(
  raw: unknown,
  ctx: ParseContext,
  entity: 'PopularFeed' | 'TrendingFeed',
): ParsedList<VideoSummary> {
  const array = asArray(raw);
  if (!array) return { items: [], dropped: [parseError(entity, null, 'wrong-type')] };
  return parseVideoSummaryList(array, ctx);
}
